import React, { useState } from 'react';

import { Box, MenuItem, MenuList, useTheme } from '@material-ui/core';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useHistory } from 'react-router-dom';
import { FiUser } from 'react-icons/fi';
import { HiOutlineTrash } from 'react-icons/hi';

import {
  AppA,
  AppDataGridWithSavedPage,
  ConfirmDialog,
  GridActions,
  GridCellWithAvatar,
} from '@vyce/core/src/components';
import { getAvatar } from '@vyce/core/src/utils/getAvatar';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { WorkerLookupDTO } from '@vyce/core/src/api/types';
import { monochrome } from '@vyce/core/src/theme/styles';

import { ChatMessageUserMenuItem } from '../../../components/Chat/ChatMessageUserMenuItem';
import useChatRequests from '../../../hooks/useChatRequests';
import { usePermissions } from '../../../hooks/usePermissions';
import { ChatUser, MANAGEMENT_PERMISSIONS } from '../../../types';
interface Props {
  tableHeight?: string;
  workers: WorkerLookupDTO[];
  loading: boolean;
  total: number;
  sortModel: GridSortModel;
  handleSortModelChange: (newModel: GridSortModel) => void;
  handlePageChange: (newPage: number) => void;
  setOffset: (offset: number) => void;
  removeWorkerFromAllLocations: (userId: string) => void;
  chatCreator?: ChatUser;
  companyId?: string;
}

export const LocationUsers: React.FC<Props> = ({
  tableHeight = '443px',
  workers,
  loading,
  total,
  sortModel,
  setOffset,
  handleSortModelChange,
  handlePageChange,
  removeWorkerFromAllLocations,
  chatCreator,
  companyId,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [closeGridAction, setCloseGridAction] = useState<boolean>(false);
  const { can } = usePermissions();
  const canManageWorkerAccounts = can(MANAGEMENT_PERMISSIONS.MANAGE_WORKER_ACCOUNTS);
  const { createPrivateChat } = useChatRequests();

  const textColor = theme.palette.type === 'dark' ? monochrome.medium : monochrome.mediumdark;

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const goToSettings = (name: string, userId: string) => {
    history.push(`/time/users/${name}_${userId}/profile`);
  };

  const handleRemoveWorkerFromAllLocations = async () => {
    if (selectedUserId) {
      setButtonLoading(true);
      await removeWorkerFromAllLocations(selectedUserId);
      setIsConfirmDeleteDialogOpen(false);
      setButtonLoading(false);
    }
  };

  const goToChat = async (timeMember: WorkerLookupDTO) => {
    if (!chatCreator || !companyId) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: {
        uid: timeMember.user_id,
        name: `${timeMember.first_name} ${timeMember.last_name}`,
        email: timeMember.email,
        phone: timeMember.phone,
        avatarUrl: timeMember.avatar,
      },
      companyId,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'first_name',
      headerName: 'Name',
      flex: 0.1,
      minWidth: 140,
      disableColumnMenu: true,
      renderCell: params => (
        <GridCellWithAvatar
          createPrivateChat={() => goToChat(params.row as WorkerLookupDTO)}
          link={`/time/users/${params.row.first_name}_${params.row.user_id}/profile`}
          target="_blank"
          avatarUrl={params.row.photo || params.row.avatar}
          avatarPlaceholder={getAvatar(params.row.gender)}
          name={`${params.row.first_name} ${params.row.last_name}`}
        />
      ),
    },
    {
      field: 'ni_number',
      headerName: 'Ref #',
      width: 95,
      flex: 0.1,
      disableColumnMenu: true,
    },
    {
      field: 'phone',
      headerName: 'Phone Number',
      disableColumnMenu: true,
      flex: 0.1,
      minWidth: 180,
      renderCell: params => <AppA content={params.row.phone} href={`tel: ${params.row.phone}`} />,
    },
    {
      field: '',
      headerName: 'Actions',
      width: 80,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%">
          <GridActions close={closeGridAction}>
            <MenuList>
              {!!chatCreator && (
                <ChatMessageUserMenuItem
                  goToChat={() => goToChat(params.row as WorkerLookupDTO)}
                  textColor={textColor}
                />
              )}
              <MenuItem onClick={() => goToSettings(params.row.first_name, params.row.user_id)}>
                <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                  <FiUser size={14} /> See user's activity
                </Box>
              </MenuItem>
              {/* The row carries no site id and the action spans every location,
                  so this is the company-wide worker-management right, not a
                  per-location one. A location-scoped manager uses the per-location
                  remove in LocationWorkers instead. */}
              {canManageWorkerAccounts && (
                <MenuItem
                  onClick={() => {
                    setSelectedUserId(params.row.user_id);
                    setIsConfirmDeleteDialogOpen(true);
                    resetCloseStatus();
                  }}>
                  <Box display="flex" alignItems="center" gridGap={8} color={textColor}>
                    <HiOutlineTrash size={14} /> Remove from all locations
                  </Box>
                </MenuItem>
              )}
            </MenuList>
          </GridActions>
        </Box>
      ),
    },
  ];

  return (
    <>
      <AppDataGridWithSavedPage
        noPaper
        rows={workers}
        loading={loading}
        height={tableHeight}
        getRowId={row => row.user_id}
        columns={columns}
        rowCount={total}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        pageSize={GRID_PAGE_SIZE}
        rowsPerPageOptions={[GRID_PAGE_SIZE]}
        disableSelectionOnClick
        setOffset={setOffset}
      />

      <ConfirmDialog
        handleClose={() => setIsConfirmDeleteDialogOpen(false)}
        open={isConfirmDeleteDialogOpen}
        confirmText="Remove"
        cancelText="No, cancel"
        loading={buttonLoading}
        title="Remove this user from all locations?"
        subtitle="Are you sure you want to remove this user from all locations? This action cannot be undone."
        handleConfirm={handleRemoveWorkerFromAllLocations}
      />
    </>
  );
};
